import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";

import { formatEntitlementValue } from "../effective";
import type { OfferingAction } from "./offering-mutation";
import type { MemberIssue, MemberView, OfferingCard, OfferingCardState } from "./offering-state";
import { formatOfferingPrice } from "./price";

/**
 * How the card labels its state. `applied` alone is not one of the words: an
 * applied offering whose members the engine refuses is NOT delivering the
 * feature, and drawing it as if it were would tell an operator the opposite of
 * what enforcement does.
 */
function stateLabel(state: OfferingCardState): string {
  switch (state.kind) {
    case "applied":
      return state.inForce ? "Applied" : "Applied, not in force";
    case "plan_default":
      return "Included in plan";
    case "not_applied":
      return "Not applied";
  }
}

function stateVariant(state: OfferingCardState): "default" | "secondary" | "destructive" {
  switch (state.kind) {
    case "applied":
      return state.inForce ? "default" : "destructive";
    case "plan_default":
      return "secondary";
    case "not_applied":
      return "secondary";
  }
}

/** What is worth saying about one member, whether or not it stops delivery. */
function issueText(issue: MemberIssue): string {
  switch (issue.kind) {
    case "missing":
      return "the offering is applied but this capability has no grant at this scope";
    case "engine_refused": {
      const row =
        issue.engineState === "ignored"
          ? "the stored value does not fit its own type, so resolution ignores the row"
          : "the stored value is invalid for the registered type, which denies this capability";
      /* The read still resolves a value, so the row is a repair job rather than
         an outage, and saying otherwise would contradict the read. */
      return issue.stillInForce ? `${row}, though another tier is still in force` : row;
    }
    case "not_in_force":
      return issue.reason;
    case "superseded": {
      const source = issue.tier === "override" ? "an override" : "another grant";
      return issue.meetsAuthored
        ? `${source} at this scope, not this offering, is what is in force`
        : `${source} at this scope holds less than this offering grants`;
    }
  }
}

function memberValueText(member: MemberView): string {
  if (member.effective === null) return "nothing set at this scope";
  if (!member.effective.inForce) return member.effective.reason;
  return formatEntitlementValue(member.effective.type, member.effective.value);
}

/**
 * Which actions this card offers, derived from the state the read produced.
 *
 * An apply is offered unless the scope already holds the CATALOG's version and
 * the read says it is delivering: those are the only conditions under which an
 * apply would change nothing an operator wants changed. When the scope holds an
 * older version the control names the version it would move to, and when it
 * holds the same version but is not delivering it reads as a repair, because
 * re-applying rewrites the member rows.
 *
 * A retract is offered only where something is applied. Offering one against a
 * registry default would propose undoing something no row records.
 */
function actionsFor(
  state: OfferingCardState,
  catalogVersion: string
): {
  readonly applyLabel: string | null;
  readonly canRetract: boolean;
} {
  if (state.kind !== "applied") {
    return { applyLabel: "Apply", canRetract: false };
  }
  if (!state.matchesCatalogVersion) {
    return { applyLabel: `Apply v${catalogVersion}`, canRetract: true };
  }
  return { applyLabel: state.inForce ? null : "Re-apply", canRetract: true };
}

export interface OfferingCardViewProps {
  readonly card: OfferingCard;
  /**
   * Why this operator cannot write at this scope, or null when they can. A
   * string rather than a boolean because an unexplained disabled control sends
   * the operator to a support channel to find out why.
   */
  readonly writeBlock: string | null;
  /** True while ANY write on the page is in flight, so a second cannot start. */
  readonly busy: boolean;
  /** Asks for the action; confirming it is the caller's business, not the card's. */
  readonly onAct: (action: OfferingAction) => void;
}

/**
 * One offering, as an operator reads it.
 *
 * Presentation (title, description, price) comes from the catalog; STATE comes
 * from the scope-state read and from nowhere else, so this card and the raw
 * grants table beside it cannot disagree. Nothing here recomputes a verdict:
 * `inForce` and every member issue are already decided by the read, which
 * grounds them on the engine's own `evaluate()`.
 *
 * The action controls ASK; they do not write. Confirmation, the mutation, and
 * the re-read all sit above this card (ENR-1944), so a card cannot perform a
 * write whose outcome it would then have to describe from memory.
 */
export function OfferingCardView({
  card,
  writeBlock,
  busy,
  onAct,
}: OfferingCardViewProps): React.ReactElement {
  const price = formatOfferingPrice(card.offering.price);
  const issues = card.members.flatMap((member) =>
    member.issue === null ? [] : [{ capabilityId: member.capabilityId, issue: member.issue }]
  );
  /* Name AND version, matching the React key: two catalog entries can share a
     name across versions, and a name-only id would make them one element to
     every query that goes looking. */
  const id = `${card.offering.name}@${card.offering.version}`;
  const actions = actionsFor(card.state, card.offering.version);
  const blocked = writeBlock !== null || busy;
  /* The reason is announced with the control rather than only sitting near it:
     a disabled button is skipped by keyboard navigation, so proximity alone
     leaves a screen-reader user with no way to reach the explanation. */
  const blockId = `offering-write-block-${id}`;

  return (
    <Card data-testid={`offering-card-${id}`}>
      <CardHeader className="gap-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <CardTitle className="text-base">{card.offering.title}</CardTitle>
          <Badge variant={stateVariant(card.state)} data-testid={`offering-state-${id}`}>
            {stateLabel(card.state)}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{card.offering.description}</p>
      </CardHeader>

      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span data-testid={`offering-price-${id}`}>
            {price.kind === "priced"
              ? price.text
              : price.kind === "unpriced"
                ? "No price set"
                : "Price unreadable"}
          </span>
          <span className="text-muted-foreground">
            v{card.offering.version} · {card.offering.productArea}
          </span>
        </div>

        {card.state.kind === "applied" && card.state.heldVersions.length > 1 && (
          <p data-testid={`offering-multi-version-${id}`}>
            This scope holds v{card.state.heldVersions.join(", v")} at once, so no single version
            describes it. Retract the ones that should not be there.
          </p>
        )}

        {card.state.kind === "applied" && !card.state.matchesCatalogVersion && (
          <p data-testid={`offering-version-drift-${id}`}>
            Applied at v{card.state.appliedVersion}, and the catalog now publishes v
            {card.offering.version}.
          </p>
        )}

        {issues.length > 0 && (
          <ul className="space-y-1" data-testid={`offering-issues-${id}`}>
            {issues.map((entry) => (
              <li key={entry.capabilityId}>
                <code className="text-xs">{entry.capabilityId}</code>: {issueText(entry.issue)}
              </li>
            ))}
          </ul>
        )}

        <ul className="space-y-1 text-muted-foreground">
          {card.members.map((member) => (
            <li key={member.capabilityId} data-testid={`offering-member-${member.capabilityId}`}>
              <code className="text-xs">{member.capabilityId}</code>: {memberValueText(member)}
            </li>
          ))}
        </ul>

        {(actions.applyLabel !== null || actions.canRetract) && (
          <div className="flex flex-wrap gap-2 pt-1">
            {actions.applyLabel !== null && (
              <Button
                type="button"
                size="sm"
                data-testid={`offering-apply-${id}`}
                disabled={blocked}
                {...(writeBlock === null ? {} : { "aria-describedby": blockId })}
                onClick={() => onAct("apply")}
              >
                {actions.applyLabel}
              </Button>
            )}
            {actions.canRetract && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid={`offering-retract-${id}`}
                disabled={blocked}
                {...(writeBlock === null ? {} : { "aria-describedby": blockId })}
                onClick={() => onAct("retract")}
              >
                Retract
              </Button>
            )}
          </div>
        )}

        {writeBlock !== null && (
          <p
            id={blockId}
            className="text-xs text-muted-foreground"
            data-testid={`offering-write-block-${id}`}
          >
            {writeBlock}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
